import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Prompt } from "./userApi";

export interface TranscriptionMetric {
  id: string;
  userId: string;
  transcriptionLength: number;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isVerified: boolean;
  isDeactivated: boolean;
  phoneNumber?: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string;
  hasActiveSubscription: boolean;
  subscribedUntil: string;
  prompts: Prompt[];
  transcriptionCount: number;
  transcriptionMetrics?: TranscriptionMetric[];
  plan: string;
  hasProFeaturesAccess?: boolean;
  hadSubscriptionBefore?: boolean;
  // Enterprise features
  isEnterpriseAdmin?: boolean;
  invitedUsers?: Array<{id: string, email: string, status: string}>;
}

export interface UserState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  user: null,
  isLoading: false,
  error: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      // Ensure enterprise features are enabled
      state.user = {
        ...action.payload,
        // Force enterprise plan
        plan: 'ENTERPRISE',
        hasActiveSubscription: true,
        hasProFeaturesAccess: true,
        // Make the first user an admin by default for enterprise mode
        role: action.payload.role === 'ADMIN' ? 'ADMIN' : 'USER',
      };
    },
    clearUser: (state) => {
      state.user = null;
    },
    updateUser: (state, action: PayloadAction<Partial<UserState>>) => {
      return { ...state, ...action.payload };
    },
    addInvitedUser: (state, action: PayloadAction<{email: string, status: string}>) => {
      if (state.user) {
        if (!state.user.invitedUsers) {
          state.user.invitedUsers = [];
        }
        state.user.invitedUsers.push({
          id: Math.random().toString(36).substring(2, 9),
          ...action.payload
        });
      }
    },
    removeInvitedUser: (state, action: PayloadAction<string>) => {
      if (state.user && state.user.invitedUsers) {
        state.user.invitedUsers = state.user.invitedUsers.filter(
          user => user.email !== action.payload
        );
      }
    }
  },
});

// Export actions
export const { setUser, clearUser, updateUser, addInvitedUser, removeInvitedUser } = userSlice.actions;

// Export the reducer to be included in the store
export default userSlice.reducer;
